import { UILabel, UISegmentedControl, UIControl, UIApplication, UITextField, UIViewController } from "uikit";
import { DLDB } from "dldb";
import { StepView, BookingStep } from "./StepView";
import { AppDelegate } from "../AppDelegate";
import { BookingPaxNoticeViewController } from "./BookingPaxNoticeViewController";

export interface BookingPaxProtocolDelegate
{
    onNumPeopleDidChange ( vc: BookingPaxViewController, numberOfPeople: number ) : void ;
}

/**
 * Standalone "number of people" step. Writes the selection to the cart (cart.pax) so any other
 * step (events, time ranges...) can react to it.
 */
export class BookingPaxViewController extends UIViewController implements BookingStep
{
    delegate : BookingPaxProtocolDelegate = null ;

    stepView: StepView;

    private paxSegmentedControl : UISegmentedControl;
    private paxTextField        : UITextField;
    private errorPeopleLabel    : UILabel;

    numPeople : number   = -1 ;
    errors    : string[] = [] ;

    viewDidLoad ( )
    {
        super.viewDidLoad();

        if ( this.paxSegmentedControl )
            this.paxSegmentedControl.addTarget( this, this.onNumPeopleChange, UIControl.Event.valueChanged );

        if ( this.paxTextField ) {
            this.paxTextField.setOnChangeText( this, this.onNumPeopleChange );
            this.paxTextField.placeholderText = "+" + ( this.segmentCount() + 1 );
        }

        this.setErrors( [] );
    }

    private segmentCount ( ) : number {
        return this.paxSegmentedControl ? this.paxSegmentedControl.layer.contents.children.length : 0;
    }

    private onNumPeopleChange ( control: UIControl )
    {
        let value = -1;

        if ( control instanceof UISegmentedControl ) {
            value = control.selectedSegmentIndex + 1;
            if ( this.paxTextField ) this.paxTextField.text = "";
        }
        else if ( control instanceof UITextField ) {
            value = parseInt( control.text, 10 );
            if ( isNaN( value ) ) value = -1;
            this.paxSegmentedControl?.selectSegmentedAtIndex( -1 );
        }

        // Venue wide maximum: show the notice and reset
        const maxPax = DLDB.shared.maxPax;
        if ( maxPax && value > maxPax ) {
            this.setNumPeople( -1 );
            this.showMaxPaxNotice( maxPax );
            return;
        }

        this.setNumPeople( value );
    }

    setNumPeople ( numberOfPeople: number )
    {
        this.numPeople = numberOfPeople;

        if ( numberOfPeople <= 0 ) {
            this.paxSegmentedControl?.selectSegmentedAtIndex( -1 );
            if ( this.paxTextField ) this.paxTextField.text = "";
        }
        else {
            this.clearError( "people" );
        }

        DLDB.shared.cart().pax = numberOfPeople > 0 ? numberOfPeople : null;
        this.delegate?.onNumPeopleDidChange( this, numberOfPeople );
    }

    private showMaxPaxNotice ( maxPax: number ) {
        let vc = new BookingPaxNoticeViewController();
        vc.initWithNibName( "BookingPaxNoticeView" );
        vc.maxPax = maxPax;
        this.presentViewController( vc, true );
    }

    public clear ( ) {
        this.numPeople = -1;
        this.paxSegmentedControl?.selectSegmentedAtIndex( -1 );
        if ( this.paxTextField ) this.paxTextField.text = "";
        DLDB.shared.cart().pax = null;
        this.setErrors( [] );
    }

    check ( ) : boolean
    {
        const valid = this.numPeople > 0;
        if ( !valid ) this.errors.push( "people" );

        if ( !(UIApplication.shared.delegate as AppDelegate).dryRunCheck ) this.setErrors( this.errors );

        return valid;
    }

    hasValue ( ) : boolean { return this.numPeople > 0; }

    setErrors ( errors: string[] )
    {
        const errorSpec = this.errorSpec( );

        for ( const err in errorSpec )
            this.clearError( err );

        for ( const err of errors )
            if ( err in errorSpec )
                errorSpec[ err ].hidden = false;
    }

    clearError ( error: string )
    {
        const errorSpec = this.errorSpec( );

        this.errors = this.errors.filter( e => e !== error );

        if ( errorSpec.hasOwnProperty( error ) )
            errorSpec[ error ].hidden = true;
    }

    private errorSpec ( )
    {
        let errorSpec = {};
        if ( this.errorPeopleLabel ) errorSpec[ "people" ] = this.errorPeopleLabel;
        return errorSpec;
    }
}
